// @flow

import React, {Component, cloneElement, Children, useEffect, useRef} from 'react';
import bowser from 'bowser';
import Animation from '@pb/kit-component-animation';
import {keyCodes} from '@pb/kit-util-key-codes';
import Portal from '@pb/kit-component-portal';
import {CANCEL_SCROLLABLE} from '@pb/kit-component-scrollable';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './busy-overlay.component.module.styl';
import {BUSY_OVERLAY, RENDER_OVERLAY_CUSTOM_EVENT, HIDE_SCROLLBAR} from './constants';

import type {Element, Node, ComponentType} from 'react';

const classNames = withCssModule(styles);

type Props = {|
    /**
     * Is shown overlay
     */
    shown: boolean,
    /**
     * Show overlay children element
     */
    showBody: boolean,
    /**
     * Mix style for overlay
     */
    mix?: string,
    containerMix?: string,
    /**
     * Is overlay should be closable
     */
    closable?: boolean,
    /**
     * onClose callback
     */
    onClose?: () => void,
    oldModalBorder?: boolean,
    /**
     * Element that would be displayed as overlay content
     */
    children: ?Element<any>,
    /**
     * Light overlay background theme
     */
    light?: boolean,
    /**
     * Portal holder id
     */
    portalHolderId?: string,
|};

type State = {|
    shown: boolean,
    showBody: boolean,
|};

type BackgroundLayerProps = {|
    children: Node,
    onClick: () => void,
    getRefCallback: (element: HTMLDivElement | null) => void,
    light?: boolean,
    mix?: string,
|};

const BackgroundLayer: ComponentType<BackgroundLayerProps> = (props: BackgroundLayerProps) => {
    const {children, getRefCallback, onClick, light, mix = ''} = props;

    const overlay = useRef<HTMLDivElement | null>(null);

    const getRef = (element: HTMLDivElement | null): void => {
        overlay.current = element;
        getRefCallback(element);
    };
    const busyOverlayClasses = classNames(
        'busy-overlay',
        CANCEL_SCROLLABLE,
        {
            'busy-overly__light': light,
            [mix]: mix
        }
    );

    useEffect(() => {
        const node = overlay.current;
        const {body} = document;

        if (node) {
            node.dispatchEvent(new Event(RENDER_OVERLAY_CUSTOM_EVENT, {bubbles: true}));
        }

        if (body) {
            body.classList.add(HIDE_SCROLLBAR);
        }

        return () => {
            if (body && body.classList.contains(HIDE_SCROLLBAR)) {
                body.classList.remove(HIDE_SCROLLBAR);
            }
        };
    }, []);

    return (
        <div className={busyOverlayClasses} onClick={onClick} ref={getRef}>{children}</div>
    );
};

/**
 * @deprecated It will be removed in 6 months 06-30-2026. Use @pb/kit-component-dialog-v2 Dialog component or Overlay instead. See dialog documentation: https://storybook.dev.pitchbookdata.com/?path=/docs/overlays-dialog-v2--documentation
 */
export const Overlay: ComponentType<Props> = (props: Props) => {
    const {
        oldModalBorder,
        children,
        light,
        shown = false,
        showBody = true,
        closable,
        mix = '',
        containerMix = '',
    } = props;

    const content = useRef<HTMLDivElement | null>(null);

    const backgroundAnimationNodeRef = useRef<HTMLDivElement | null>(null);
    const contentAnimationNodeRef = useRef<HTMLDivElement | null>(null);

    if (!children) {
        return null;
    }

    const trigger = Children.only(children);
    const {onClose} = trigger.props;

    const onClickOverlay = () => {
        if (closable && onClose) {
            onClose();
        }
    };

    const getRef = (element: HTMLDivElement | null): void => {
        content.current = element;
        backgroundAnimationNodeRef.current = element;
    };

    const onEntered = () => {
        const node = content.current;

        if (node) {
            node.style.opacity = '1';

            setTimeout(() => {
                node.style.opacity = '';
            }, 300);
        }

        /*
            solution for bug of `react-transition-group ` in IE
            report: https://github.com/reactjs/react-transition-group/issues/189
            TODO: remove when IE will not be supported
        * */
    };

    const overlayClasses = classNames('busy-overlay__container overlay',
        containerMix,
        {
            overlay_border: oldModalBorder // TODO: remove prop oldModalBorder after deleting iframe,
        }
    );

    return (
        <Animation
            nodeRef={backgroundAnimationNodeRef}
            appear={true}
            classNames={{
                appear: styles['busy-overlay-appear'],
                appearActive: styles['busy-overlay-appear-active'],
                enter: styles['busy-overlay-enter'],
                enterActive: styles['busy-overlay-enter-active'],
                exit: styles['busy-overlay-exit'],
                exitActive: styles['busy-overlay-exit-active'],
                exitDone: styles['busy-overlay-exit-done']
            }} {...(bowser.msie ? {onEntered} : null)}>
            {shown ?
                <BackgroundLayer getRefCallback={getRef} onClick={onClickOverlay} light={light} mix={mix}>
                    <Animation
                        nodeRef={contentAnimationNodeRef}
                        appear={true}
                        classNames={{
                            enter: styles['busy-overlay__container-enter'],
                            enterActive: styles['busy-overlay__container-enter-active'],
                            exit: styles['busy-overlay__container-exit'],
                            exitActive: styles['busy-overlay__container-exit-active'],
                            appearDone: 'busy-overlay__container-appear-done',
                            enterDone: 'busy-overlay__container-enter-done',
                        }}>
                        {showBody ? <div
                            ref={contentAnimationNodeRef}
                            className={overlayClasses}
                            onClick={e => e.stopPropagation()}>
                            {cloneElement(children, {onClose})}
                        </div> : null}
                    </Animation>
                </BackgroundLayer> : null}
        </Animation>
    );
};

type BusyOverlayState = State & {
    closable?: boolean,
};

/**
 * @deprecated It will be removed in 6 months 06-30-2026. Use @pb/kit-component-dialog-v2 Dialog component or Overlay instead. See dialog documentation: https://storybook.dev.pitchbookdata.com/?path=/docs/overlays-dialog-v2--documentation
 */
export class BusyOverlay extends Component<Props, BusyOverlayState> {
    static defaultProps = {
        shown: false,
        showBody: true,
        closable: false,
    };

    componentDidMount() {
        if (this.props.shown) {
            this.bindEventsListeners();
        }
    }

    componentDidUpdate() {
        if (this.props.shown) {
            this.bindEventsListeners();
        } else {
            this.unbindEventsListeners();
        }
    }

    componentWillUnmount() {
        this.unbindEventsListeners();
    }

    closeByEscape = (e: KeyboardEvent) => {
        const {onClose} = this.props;
        const {KEY_ESCAPE, KEY_ESC} = keyCodes;

        if (onClose && [KEY_ESCAPE, KEY_ESC].some(k => k === e.key)) {
            onClose();
        }
    };

    bindEventsListeners() {
        document.addEventListener('keyup', this.closeByEscape);
        window.addEventListener('mousedown', this.onClickAway, true);
    }

    unbindEventsListeners() {
        document.removeEventListener('keyup', this.closeByEscape);
        window.removeEventListener('mousedown', this.onClickAway, true);
    }

    onClickAway = (e: Event) => {
        const {onClose} = this.props;
        if (e.target instanceof HTMLElement
            && onClose
            && e.target.classList
            && e.target.classList.contains(BUSY_OVERLAY)) {
            e.stopPropagation();
            onClose();
        }
    };

    render() {
        const {shown, showBody, mix, oldModalBorder, light, closable, containerMix, portalHolderId} = this.props;
        return (
            <Portal {...portalHolderId ? {holderId: portalHolderId} : null}>
                <Overlay
                    {...{
                        shown,
                        showBody,
                        mix,
                        oldModalBorder, // TODO: remove prop oldModalBorder after deleting iframe
                        light,
                        closable,
                        containerMix,
                    }}>
                    {this.props.children}
                </Overlay>
            </Portal>
        );
    }
}

BusyOverlay.displayName = 'BusyOverlay';
